import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import api, { fileUrl } from './api';
import { StarsDisplay } from './Stars';
import { fmtUK, UKDatePicker } from './UKDate';

const CATS = ['Plumbing', 'Electrical', 'Painting & Decorating', 'House Cleaning', 'Commercial Cleaning', 'HVAC'];
const fmt = (iso) => fmtUK(iso);
const PAGE = 10;

const toCSV = (rows, headers) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map((h) => esc(h.label)).join(',')];
  rows.forEach((r) => lines.push(headers.map((h) => esc(h.get(r))).join(',')));
  return lines.join('\n');
};
const download = (name, text) => {
  const blob = new Blob([text], { type: 'text/csv' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
};

const JobHistory = ({ role }) => {
  const [data, setData] = useState({ jobs: [], total: 0, avgRating: null });
  const [cat, setCat] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get(`/my/completed${showArchived ? '?archived=true' : ''}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);
  const archive = async (id) => { try { await api.post(`/jobs/${id}/archive`); load(); } catch (e) {} };
  const unarchive = async (id) => { try { await api.post(`/jobs/${id}/unarchive`); load(); } catch (e) {} };
  const bulkArchive = async () => {
    if (!window.confirm('Archive all completed jobs older than 30 days? They stay available under "View Archived".')) return;
    try { const r = await api.post('/jobs/bulk-archive'); load(); alert(`${r.data.archived} job(s) archived.`); } catch (e) {}
  };

  const filtered = useMemo(() => data.jobs.filter((j) => {
    if (cat && j.category !== cat) return false;
    if (from && (j.completedAt || '') < from) return false;
    if (to && (j.completedAt || '') > to + 'T23:59:59') return false;
    return true;
  }), [data.jobs, cat, from, to]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const rows = filtered.slice((page - 1) * PAGE, page * PAGE);

  const exportCSV = () => {
    if (role === 'client') {
      download('completed-jobs.csv', toCSV(filtered, [
        { label: 'Date', get: (r) => fmt(r.completedAt) }, { label: 'Reference', get: (r) => r.ref },
        { label: 'Service', get: (r) => r.category }, { label: 'Operative', get: (r) => r.operativeName },
        { label: 'Amount Paid (£)', get: (r) => r.amount }]));
    } else {
      download('completed-jobs.csv', toCSV(filtered, [
        { label: 'Date', get: (r) => fmt(r.completedAt) }, { label: 'Reference', get: (r) => r.ref },
        { label: 'Service', get: (r) => r.category }, { label: 'Client', get: (r) => r.clientFirstName },
        { label: 'Gross (£)', get: (r) => r.grossValue }, { label: 'EA Commission (£)', get: (r) => r.commission },
        { label: 'Net Earnings (£)', get: (r) => r.netEarnings }]));
    }
  };

  return (
    <div className="mt-10" data-testid="job-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-[#1b4f72]">{role === 'client' && showArchived ? 'Archived Jobs' : 'Completed Jobs'} <span className="text-sm font-normal text-gray-400">({data.total})</span>{role === 'operative' && data.avgRating != null && <span className="ml-2 inline-flex items-center gap-1 align-middle text-sm text-amber-500"><StarsDisplay value={data.avgRating} size={13} /> {data.avgRating.toFixed(1)}</span>}</h2>
        <div className="flex items-center gap-2">
          {role === 'client' && <button data-testid="toggle-archived-btn" onClick={() => { setShowArchived((s) => !s); setPage(1); }} className="rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-600 hover:bg-white">{showArchived ? 'View Active' : 'View Archived'}</button>}
          {role === 'client' && !showArchived && <button data-testid="bulk-archive-btn" onClick={bulkArchive} className="rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-600 hover:bg-white">Archive all &gt; 30 days</button>}
          {filtered.length > 0 && <button data-testid="export-csv-btn" onClick={exportCSV} className="flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-600 hover:bg-white"><Download size={14} /> Export CSV</button>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div><label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">Service</label><select data-testid="history-cat-filter" value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"><option value="">All Services</option>{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div><label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">From</label><div className="w-36"><UKDatePicker testId="history-from" value={from} onChange={(v) => { setFrom(v); setPage(1); }} /></div></div>
        <div><label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">To</label><div className="w-36"><UKDatePicker testId="history-to" value={to} onChange={(v) => { setTo(v); setPage(1); }} /></div></div>
      </div>
      {rows.length === 0 ? <p className="mt-3 rounded-2xl bg-white p-6 text-sm text-gray-500 shadow">No completed jobs yet.</p> : (
        <div className="mt-4 space-y-3">
          {rows.map((j) => (
            <div key={j.jobId} data-testid={`history-${j.jobId}`} className="rounded-2xl bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#1b4f72]">{j.title} <span className="text-xs font-normal text-gray-400">· {j.category} · {j.ref}</span></p>
                  {role === 'client' ? (
                    <p className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                      {j.operativePhoto && <img src={fileUrl(j.operativePhoto)} alt="" className="h-6 w-6 rounded-full object-cover" />}
                      {j.operativeName} <span className="text-xs text-gray-400">({j.operativeReg})</span></p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-600">Client: {j.clientFirstName} · Net earnings £{j.netEarnings} <span className="text-xs text-gray-400">(gross £{j.grossValue}, commission £{j.commission})</span></p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">Completed {fmt(j.completedAt)}{role === 'client' ? ` · paid £${j.amount}` : ''}</p>
                  {j.rating && <div className="mt-1 flex items-center gap-1"><StarsDisplay value={j.rating} size={13} />{role === 'operative' && j.reviewComment && <span className="ml-2 text-xs text-gray-500">"{j.reviewComment}"</span>}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 text-sm">
                  <Link to={`/jobs/${j.jobId}`} className="font-semibold text-[#3a9b5c] hover:underline">View Job</Link>
                  {role === 'client' && <Link to={`/professionals/${j.operativeId}`} className="text-[#1b4f72] hover:underline">View Profile</Link>}
                  {role === 'client' && <Link to={`/post-a-job?category=${encodeURIComponent(j.category)}`} data-testid={`book-again-${j.jobId}`} className="rounded-full bg-[#e9f3ec] px-3 py-1 text-xs font-semibold text-[#3a9b5c]">Book Again</Link>}
                  {role === 'client' && (j.archived
                    ? <button data-testid={`unarchive-${j.jobId}`} onClick={() => unarchive(j.jobId)} className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-50">Unarchive</button>
                    : <button data-testid={`archive-${j.jobId}`} onClick={() => archive(j.jobId)} className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-50">Archive</button>)}
                </div>
              </div>
            </div>
          ))}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-full border border-gray-300 px-3 py-1 text-sm disabled:opacity-40">Prev</button>
              <span className="text-sm text-gray-500">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-full border border-gray-300 px-3 py-1 text-sm disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobHistory;
